import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "../../../../../../../utils/axiosInceptor";
import { ApiResponse } from "../../../../../../../types/api-response";
import { apiV1Endpoint } from "../../../../../../../api/v1";
import { TorScheduleRow } from "./types";

type UseTorScheduleParams = {
  leaseId: string;
  initialYear?: number;
  /** When false, the query does not run (e.g. until the TOR Schedule modal is opened). */
  isTorSchedule?: boolean;
};

const getBillingSchedule = async (leaseId: string, year: number): Promise<TorScheduleRow[]> => {
  const url = `${apiV1Endpoint}/booking/generateCommercialTorSchedule`;

  const response = await axios.post<ApiResponse<TorScheduleRow[]>>(url, {
    leaseId,
    year,
  });
  return response.data.result;
};

export const useTorSchedulePreview = ({ leaseId, initialYear = 1, isTorSchedule }: UseTorScheduleParams) => {
  const [year, setYear] = useState<number>(initialYear);

  const query = useQuery({
    queryKey: ["torSchedule", leaseId],
    enabled: Boolean(leaseId && isTorSchedule),
    queryFn: () => getBillingSchedule(leaseId, year),
    staleTime: 1000 * 60 * 5,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const handleYearChange = (newYear: number) => {
    setYear(newYear);
  };

  const filteredData = useMemo(() => {
    if (!query.data) return [];
    return query.data.filter((item) => item.noOfYear === year);
  }, [query.data, year]);

  const yearOptions = useMemo(() => {
    const map = new Map();

    (query?.data || []).forEach((item) => {
      const year = item?.noOfYear ?? 0;
      if (!map.has(year)) {
        map.set(year, {
          label: `Year ${year || "-"}`,
          value: year,
        });
      }
    });

    return Array.from(map.values());
  }, [query.data]);
  // 3. Show dropdown flag
  const showYearOption = yearOptions.length > 1;

  return {
    year,
    setYear: handleYearChange,

    // safe fallback handling
    data: filteredData ?? [],
    isSuccess: query.isSuccess,
    yearOptions: yearOptions,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    showTorYearOption: showYearOption,
    error: query.error ?? [],
  };
};
